import React from 'react';
import { useTranslationsV2 as useTranslations } from '@unisporkal/localization';
import { useSiteMap } from '@unisporkal/sites';
import useSelectedIndex from '../../../../hooks/stateful/useSelectedIndex';
import AssetOptions from '../../AssetOptions/AssetOptions';
import UsageRestrictionsWarning from '../../UsageRestrictionsWarning/UsageRestrictionsWarning';
import ViewOnlyWarning from '../../ViewOnlyWarning/ViewOnlyWarning';
import MultipleAgreements from '../../MultipleAgreements/MultipleAgreements';
import AssetSizes from '../../AssetSizes/AssetSizes';
import VideoTemplate from '../../VideoTemplate/VideoTemplate';
import MiniDownloadHistory from '../../MiniDownloadHistory/MiniDownloadHistory';
import ProjectCodes from '../../ProjectCodes/ProjectCodes';
import Notes from '../../Notes/Notes';
import DownloadMessage from '../../Messages/DownloadMessage/DownloadMessage';
import UnlimitedContentMessage from '../../Messages/UnlimitedContentMessage/UnlimitedContentMessage';
import useMode from '../../../../hooks/stateful/useMode';
import acquisitionMode from '../../../../support/acquisitionMode';
import { useAsset } from '../../../../hooks/stateful/useAsset';
import useOnLoad from '../../../../hooks/queries/useOnLoad';
import { trackClickClientLicenses } from '../../../../support/ctaTracking';

const DownloadForm: React.FC = () => {
  const { mode } = useMode();
  const t = useTranslations();
  const siteMap = useSiteMap();

  const {
    asset: { isVideoTemplate },
  } = useAsset();

  const { selectedOption: selectedAgreementOption } = useSelectedIndex();

  const premiumAccessSelected =
    selectedAgreementOption?.type === 'PremiumAccess';

  const easyAccessSelected = selectedAgreementOption?.type === 'EasyAccess';

  const {
    response: {
      config: {
        isAgencyUnlimitedCreativeBuyer,
        isAgencyUnlimitedArtBuyer,
      } = {},
    } = {},
  } = useOnLoad();

  const showCreativeBuyerMessage =
    Boolean(isAgencyUnlimitedCreativeBuyer) && easyAccessSelected;

  const showArtBuyerMessage =
    Boolean(isAgencyUnlimitedArtBuyer) && premiumAccessSelected;

  const buyCardPAMessageText = (
    <>
      {t('ready_to_client_license_page', {
        links: [
          {
            href: siteMap.clientLicenses(),
            onClick: trackClickClientLicenses,
            role: 'link',
          },
        ],
      })}
    </>
  );

  const buyCardEZAMessageText = t('download_unlimited_share_admin_for_license');

  if (showArtBuyerMessage) {
    return (
      <div data-testid="download-form">
        <AssetOptions />
        <UnlimitedContentMessage buyCardMessageText={buyCardPAMessageText} />
        <MiniDownloadHistory />
      </div>
    );
  }

  return (
    <div data-testid="download-form">
      <AssetOptions />
      <DownloadMessage isAgencyUnlimitedPilot={showCreativeBuyerMessage} />
      {mode === acquisitionMode.MODAL && <UsageRestrictionsWarning />}
      {showCreativeBuyerMessage && (
        <UnlimitedContentMessage buyCardMessageText={buyCardEZAMessageText} />
      )}
      <ViewOnlyWarning />
      <MultipleAgreements />
      {isVideoTemplate() ? <VideoTemplate /> : <AssetSizes />}
      <MiniDownloadHistory />
      <ProjectCodes />
      <Notes />
    </div>
  );
};

export default DownloadForm;
